import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';

const STATUSES = [
  { key: 'draft',     label: 'Entwurf',      color: 'var(--text3)' },
  { key: 'confirmed', label: 'Bestätigt',    color: '#3b82f6' },
  { key: 'closed',    label: 'Abgeschlossen',color: '#22c55e' },
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CHF', 'AED'];

const EMPTY = {
  deal_reference: '', title: '', cargo_desc: '', quantity: '', currency: 'USD',
  purchase_price: '', selling_price: '', commission_income: '',
  freight_cost: '', insurance_cost: '', inspection_cost: '',
  demurrage_cost: '', financing_cost: '', other_costs: '',
  gross_margin: '', net_profit: '', exchange_rate: '', period: '',
  status: 'draft', notes: '',
};

function parseNum(s) {
  const n = parseFloat(String(s || '').replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
}

function fmtNum(n, currency = 'USD') {
  if (!n && n !== 0) return '—';
  return n.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ' + currency;
}

function StatusBadge({ status }) {
  const m = STATUSES.find(s => s.key === status) || STATUSES[0];
  return (
    <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 700, background: m.color + '22', color: m.color }}>
      {m.label}
    </span>
  );
}

function CalcRow({ label, value, currency, accent, danger, border }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '8px 0', borderBottom: border ? '1px solid var(--border)' : 'none',
      fontSize: 13 }}>
      <span style={{ color: 'var(--text2)' }}>{label}</span>
      <span style={{
        fontWeight: accent || danger ? 700 : 500,
        color: danger ? 'var(--danger)' : accent ? 'var(--accent)' : 'var(--text1)',
      }}>
        {fmtNum(value, currency)}
      </span>
    </div>
  );
}

function PLModal({ record, onClose, onSaved }) {
  const [form, setForm] = useState(record ? { ...record } : { ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const calc = () => {
    const revenue  = parseNum(form.selling_price) + parseNum(form.commission_income);
    const purchase = parseNum(form.purchase_price);
    const costs    = ['freight_cost','insurance_cost','inspection_cost','demurrage_cost','financing_cost','other_costs']
      .reduce((s, k) => s + parseNum(form[k]), 0);
    return { revenue, purchase, costs, gross: revenue - purchase, net: revenue - purchase - costs };
  };

  const c = calc();

  const handleSave = async () => {
    if (!form.title.trim()) { setErr('Titel erforderlich'); return; }
    setSaving(true); setErr('');
    try {
      if (record) await API.put(`/pl/${record.id}`, form);
      else        await API.post('/pl', form);
      onSaved(); onClose();
    } catch (e) { setErr(e.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 800 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">{record ? 'P&L bearbeiten' : 'Neue P&L-Aufstellung'}</div>
        {err && <div className="error-msg">{err}</div>}

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Titel *</label>
            <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="z.B. Crude Oil Q2 2026" />
          </div>
          <div className="form-group">
            <label className="form-label">Deal-Referenz</label>
            <input className="form-input" value={form.deal_reference} onChange={e => set('deal_reference', e.target.value)} />
          </div>
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label">Cargo</label>
            <input className="form-input" value={form.cargo_desc} onChange={e => set('cargo_desc', e.target.value)} placeholder="z.B. Crude Oil D6" />
          </div>
          <div className="form-group">
            <label className="form-label">Menge</label>
            <input className="form-input" value={form.quantity} onChange={e => set('quantity', e.target.value)} placeholder="z.B. 50,000 MT" />
          </div>
          <div className="form-group">
            <label className="form-label">Währung</label>
            <select className="form-input" value={form.currency} onChange={e => set('currency', e.target.value)}>
              {CURRENCIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          {/* Revenue */}
          <div className="card" style={{ margin: 0 }}>
            <div className="card-title" style={{ color: 'var(--accent)' }}>📈 Erlöse</div>
            {[
              ['Einkaufspreis', 'purchase_price'],
              ['Verkaufspreis', 'selling_price'],
              ['Provision / Kommission', 'commission_income'],
            ].map(([label, key]) => (
              <div className="form-group" key={key} style={{ marginBottom: 8 }}>
                <label className="form-label">{label}</label>
                <input className="form-input" value={form[key]} onChange={e => set(key, e.target.value)} placeholder="0" />
              </div>
            ))}
          </div>
          {/* Costs */}
          <div className="card" style={{ margin: 0 }}>
            <div className="card-title" style={{ color: 'var(--danger)' }}>📉 Kosten</div>
            {[
              ['Fracht', 'freight_cost'],
              ['Versicherung', 'insurance_cost'],
              ['Inspektion', 'inspection_cost'],
              ['Demurrage', 'demurrage_cost'],
              ['Finanzierung', 'financing_cost'],
              ['Sonstige', 'other_costs'],
            ].map(([label, key]) => (
              <div className="form-group" key={key} style={{ marginBottom: 8 }}>
                <label className="form-label">{label}</label>
                <input className="form-input" value={form[key]} onChange={e => set(key, e.target.value)} placeholder="0" />
              </div>
            ))}
          </div>
        </div>

        {/* Live calc */}
        <div className="card" style={{ background: 'var(--bg-deep)', margin: '0 0 16px' }}>
          <div className="card-title">📊 Kalkulation (live)</div>
          <CalcRow label="Umsatz (Verkauf + Provision)" value={c.revenue} currency={form.currency} border />
          <CalcRow label="Einkauf" value={c.purchase} currency={form.currency} border />
          <CalcRow label="Bruttomarge" value={c.gross} currency={form.currency} border accent={c.gross > 0} danger={c.gross < 0} />
          <CalcRow label="Gesamtkosten" value={c.costs} currency={form.currency} border />
          <CalcRow label="Nettogewinn" value={c.net} currency={form.currency} accent={c.net > 0} danger={c.net < 0} />
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label">Periode (JJJJ-MM)</label>
            <input className="form-input" value={form.period} onChange={e => set('period', e.target.value)} placeholder="2026-03" />
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
              {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Wechselkurs</label>
            <input className="form-input" value={form.exchange_rate} onChange={e => set('exchange_rate', e.target.value)} placeholder="z.B. 1.08 EUR/USD" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Notizen</label>
          <textarea className="form-input" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PLPage() {
  const [records, setRecords]   = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [q, setQ]               = useState('');
  const [statusFilter, setStatus] = useState('');
  const [modal, setModal]       = useState(null); // null | 'create' | record

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (q) params.q = q;
      const [recs, sum] = await Promise.all([
        API.get('/pl', { params }),
        API.get('/pl/summary'),
      ]);
      setRecords(recs.data);
      setSummary(sum.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (rec) => {
    if (!window.confirm(`"${rec.title}" wirklich löschen?`)) return;
    await API.delete(`/pl/${rec.id}`);
    load();
  };

  return (
    <div className="page-content">
      {modal && (
        <PLModal
          record={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }} />
      )}

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>📊 P&L Tracking</h1>
          <p>Gewinn- & Verlustrechnung pro Cargo / Deal</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('create')}>+ Neue Aufstellung</button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid-4" style={{ marginBottom: 20 }}>
          {[
            { label: 'Gesamt Umsatz',  value: summary.total_revenue, color: '#3b82f6', prefix: '$' },
            { label: 'Bruttomarge',    value: summary.total_gross,   color: '#f59e0b', prefix: '$' },
            { label: 'Gesamtkosten',   value: summary.total_costs,   color: 'var(--danger)', prefix: '$' },
            { label: 'Nettogewinn',    value: summary.total_net,     color: summary.total_net >= 0 ? 'var(--accent)' : 'var(--danger)', prefix: '$' },
          ].map(c => (
            <div key={c.label} className="card stat-card">
              <div className="stat-value" style={{ fontSize: 22, color: c.color }}>
                {c.prefix}{Math.abs(c.value).toLocaleString('de-DE', { maximumFractionDigits: 0 })}
                {c.value < 0 && <span style={{ fontSize: 14 }}> (neg.)</span>}
              </div>
              <div className="stat-label">{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input className="form-input" style={{ maxWidth: 260 }} placeholder="Suche Titel, Deal…"
          value={q} onChange={e => setQ(e.target.value)} />
        <select className="form-input" style={{ maxWidth: 180 }} value={statusFilter} onChange={e => setStatus(e.target.value)}>
          <option value="">Alle Status</option>
          {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={load}>⟳</button>
      </div>

      {loading && <div className="loading">Lade…</div>}

      {!loading && records.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Keine P&L-Aufstellungen</div>
        </div>
      )}

      <table className="tbl">
        {records.length > 0 && (
          <thead>
            <tr>
              <th>Titel</th>
              <th>Deal</th>
              <th>Periode</th>
              <th>Umsatz</th>
              <th>Nettogewinn</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
        )}
        <tbody>
          {records.map(r => (
            <tr key={r.id}>
              <td>
                <div style={{ fontWeight: 600 }}>{r.title}</div>
                {r.cargo_desc && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{r.cargo_desc}</div>}
              </td>
              <td style={{ color: 'var(--text2)' }}>{r.deal_reference || '—'}</td>
              <td style={{ color: 'var(--text2)' }}>{r.period || '—'}</td>
              <td>{r.calc?.revenue ? fmtNum(r.calc.revenue, r.currency) : '—'}</td>
              <td>
                <span style={{ fontWeight: 700, color: (r.calc?.net || 0) >= 0 ? 'var(--accent)' : 'var(--danger)' }}>
                  {r.calc ? fmtNum(r.calc.net, r.currency) : '—'}
                </span>
              </td>
              <td><StatusBadge status={r.status} /></td>
              <td>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setModal(r)}>✏</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(r)}>🗑</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
